import { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Brain, GraduationCap, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ExamConfigFormValues, Question, Section } from '../../types/exam-config.types';
import { useExamConfig, useUpdateExamConfig } from '../../hooks/useExamConfig';
import { useGenerateQuestion, useAssignSkill } from '../../hooks/useQuestionGeneration';
import { useSuggestedSkills } from '../../hooks/useSuggestedSkills';
import { QuestionEditor } from './QuestionEditor';
import { AddQuestionDropdown } from './AddQuestionDropdown';
import { ImportFromLibrary } from './ImportFromLibrary';
import { PreviewModal } from './PreviewModal';
import { ExamSectionRail, type SectionTab } from './ExamSectionRail';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { Button } from '@/components/ui/atoms/Button';
import {
  usePsychometricTests,
  useAddPsychometricSection,
  useRemovePsychometricSection,
} from '../../hooks/usePsychometricTests';

export interface ExamConfigEditorHandle {
  /** Programmatically trigger a save — used by parent dialogs (e.g. "Save & leave"). */
  save: () => void;
}

interface ExamConfigEditorProps {
  jobId: string;
  onSaveSuccess?: () => void; // optional: called after successful save, enables post-save navigation
  onBack?: () => void; // optional: show a back link in the header
  onDirtyChange?: (dirty: boolean) => void; // optional: reports unsaved-changes state to the parent
}

// Skills flagged as per-candidate (AI customizes per CV)
const PER_CANDIDATE_SKILLS = new Set([
  'Core Concepts Strengths',
  'Core Concepts Weaknesses',
  'Tradeoffs',
]);

// Core skills always available for Technical Skills section (regardless of LLM suggestions)
const CORE_SKILL_NAMES = ['Core Concepts Strengths', 'Core Concepts Weaknesses', 'Tradeoffs'];

const SECTION_META: Record<string, { number: string; icon: 'technical' | 'soft' }> = {
  'Technical Skills': { number: '01', icon: 'technical' },
  'Soft Skills': { number: '02', icon: 'soft' },
};

const SECTION_TITLE_KEYS: Record<string, 'exam_config.sections.technical_skills' | 'exam_config.sections.soft_skills'> = {
  'Technical Skills': 'exam_config.sections.technical_skills',
  'Soft Skills': 'exam_config.sections.soft_skills',
};

const buildOrderedSections = (sections: Section[], visualOrderMap: Record<string, string[]>): Section[] => {
  return sections.map((section) => {
    const visualOrder = visualOrderMap[section.id];
    if (!visualOrder || visualOrder.length === 0) return section;

    const questionToSkill = new Map<string, string>();
    for (const skill of section.skills) {
      for (const q of skill.questions) {
        questionToSkill.set(q.id, skill.id);
      }
    }

    const questionById = new Map<string, Question>();
    for (const skill of section.skills) {
      for (const q of skill.questions) {
        questionById.set(q.id, q);
      }
    }

    const skillQuestions = new Map<string, Question[]>();
    for (const skill of section.skills) {
      skillQuestions.set(skill.id, []);
    }
    // Add questions in visual order first
    const orderedSet = new Set<string>();
    for (const qId of visualOrder) {
      const skillId = questionToSkill.get(qId);
      const q = questionById.get(qId);
      if (skillId && q) {
        skillQuestions.get(skillId)!.push(q);
        orderedSet.add(qId);
      }
    }
    // Then append any questions not yet in the visual order (e.g. newly created skills)
    for (const [qId, q] of questionById) {
      if (!orderedSet.has(qId)) {
        const skillId = questionToSkill.get(qId);
        if (skillId) {
          skillQuestions.get(skillId)!.push(q);
        }
      }
    }

    const sortedSkills = section.skills
      .map((skill) => ({ ...skill, questions: skillQuestions.get(skill.id) ?? [] }))
      .filter((skill) => skill.questions.length > 0)
      .sort((a, b) => {
        const aIdx = visualOrder.indexOf(a.questions[0].id);
        const bIdx = visualOrder.indexOf(b.questions[0].id);
        // Questions not in visualOrder sort to the end (indexOf returns -1)
        const aPos = aIdx === -1 ? Infinity : aIdx;
        const bPos = bIdx === -1 ? Infinity : bIdx;
        return aPos - bPos;
      });

    return { ...section, skills: sortedSkills };
  });
};

export const ExamConfigEditor = forwardRef<ExamConfigEditorHandle, ExamConfigEditorProps>(function ExamConfigEditor({ jobId, onSaveSuccess, onBack, onDirtyChange }, ref) {
  const { t } = useTranslation('job');
  const { data } = useExamConfig(jobId);
  const updateMutation = useUpdateExamConfig(jobId);
  const generateQuestion = useGenerateQuestion(jobId);
  const assignSkill = useAssignSkill(jobId);
  const { data: suggestedSkills } = useSuggestedSkills(jobId);

  const [loadingQuestionIds, setLoadingQuestionIds] = useState<Set<string>>(new Set());
  const [aiGeneratedIds, setAiGeneratedIds] = useState<Set<string>>(new Set());
  const [unassignedIds, setUnassignedIds] = useState<Set<string>>(new Set());
  const [assigningSkillIds, setAssigningSkillIds] = useState<Set<string>>(new Set());
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [showLanguageWarning, setShowLanguageWarning] = useState(false);
  // Visual order overrides per section (keyed by section ID → ordered question IDs)
  const [visualOrderMap, setVisualOrderMap] = useState<Record<string, string[]>>({});
  const [hasVisualReorder, setHasVisualReorder] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [previewTestId, setPreviewTestId] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const lastQuestionRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);

  // Psychometric test hooks
  const { data: allPsychometricTests = [] } = usePsychometricTests();
  const addPsychometricMutation = useAddPsychometricSection(jobId);
  const removePsychometricMutation = useRemovePsychometricSection(jobId);

  const { handleSubmit, reset, watch, setValue, getValues, formState: { isDirty } } = useForm<ExamConfigFormValues>({
    defaultValues: {
      language: 'EN',
      sections: [],
      interviewExpiryDays: 7,
    },
  });

  useImperativeHandle(ref, () => ({
    save: () => handleSubmit(onSubmit)(),
  }));

  // Report unsaved-changes state up so the parent can guard tab switches.
  useEffect(() => {
    onDirtyChange?.(isDirty || hasVisualReorder);
  }, [isDirty, hasVisualReorder, onDirtyChange]);

  // Only reset form with backend data on initial load, not on every refetch
  useEffect(() => {
    if (data && !initialDataLoaded) {
      const loadedSections = data.config_data.sections;
      reset({
        language: data.language,
        sections: loadedSections,
        interviewExpiryDays: data.interview_expiry_days ?? 7,
      });
      // Eagerly initialize visual order from loaded data so we never fall back to skill-grouped default
      const initialOrder: Record<string, string[]> = {};
      for (const section of loadedSections) {
        initialOrder[section.id] = section.skills.flatMap((s: any) => s.questions.map((q: any) => q.id));
      }
      setVisualOrderMap(initialOrder);
      setInitialDataLoaded(true);
    }
  }, [data, initialDataLoaded, reset]);

  const sections = watch('sections') || [];
  const language = watch('language');
  const interviewExpiryDays = watch('interviewExpiryDays');

  const handleExpiryChange = useCallback((days: number) => {
    setValue('interviewExpiryDays', days, { shouldDirty: true });
  }, [setValue]);

  const onSubmit = (formData: ExamConfigFormValues) => {
    const orderedSections = buildOrderedSections(formData.sections, visualOrderMap);
    // Preserve psychometric_sections from server data so Save does not wipe them
    const existingPsychometric = data?.config_data?.psychometric_sections;
    const configData: { sections: typeof orderedSections; psychometric_sections?: typeof existingPsychometric } = { sections: orderedSections };
    if (existingPsychometric && existingPsychometric.length > 0) {
      configData.psychometric_sections = existingPsychometric;
    }
    updateMutation.mutate(
      {
        language: formData.language,
        config_data: configData,
        interview_expiry_days: formData.interviewExpiryDays,
      },
      {
        onSuccess: () => {
          reset({ ...formData, sections: orderedSections });
          setHasVisualReorder(false);
          onSaveSuccess?.();
        },
      },
    );
  };

  // Build flat list of all questions, respecting visual order overrides from drag-and-drop
  const allQuestionItems: { question: Question; sectionIndex: number; skillIndex: number; questionIndex: number; skillName: string }[] = [];
  for (let si = 0; si < sections.length; si++) {
    // Build a lookup of all questions in this section
    const sectionQuestionMap = new Map<string, { question: Question; skillIndex: number; questionIndex: number; skillName: string }>();
    for (let ski = 0; ski < sections[si].skills.length; ski++) {
      const skill = sections[si].skills[ski];
      for (let qi = 0; qi < skill.questions.length; qi++) {
        sectionQuestionMap.set(skill.questions[qi].id, {
          question: skill.questions[qi],
          skillIndex: ski,
          questionIndex: qi,
          skillName: skill.name,
        });
      }
    }

    const visualOrder = visualOrderMap[sections[si].id];
    let orderedIds: string[];

    if (visualOrder) {
      // Use visual order, but also append any new questions not yet in the order
      const visualSet = new Set(visualOrder);
      const newIds = [...sectionQuestionMap.keys()].filter((id) => !visualSet.has(id));
      orderedIds = [...visualOrder.filter((id) => sectionQuestionMap.has(id)), ...newIds];
    } else {
      // Default: grouped by skill
      orderedIds = [...sectionQuestionMap.keys()];
    }

    for (const qId of orderedIds) {
      const item = sectionQuestionMap.get(qId);
      if (item) {
        allQuestionItems.push({ ...item, sectionIndex: si });
      }
    }
  }

  // ── Flow A: Add question with skill → AI generates text ──
  // skillIdOrName can be an existing skill ID or a new skill name from the pool
  const handleAddWithSkill = useCallback((sectionIndex: number, skillIdOrName: string) => {
    let skillIndex = sections[sectionIndex].skills.findIndex((s) => s.id === skillIdOrName);
    let skillName: string;

    // If skill doesn't exist in section, create it
    if (skillIndex === -1) {
      // skillIdOrName is a skill name from the pool
      skillName = skillIdOrName;
      const newSkillId = uuid();
      const newSkill = { id: newSkillId, name: skillName, questions: [] as Question[] };
      const updatedSkills = [...sections[sectionIndex].skills, newSkill];
      setValue(`sections.${sectionIndex}.skills`, updatedSkills, { shouldDirty: true });
      skillIndex = updatedSkills.length - 1;
    } else {
      skillName = sections[sectionIndex].skills[skillIndex].name;
    }

    const newId = uuid();
    const newQuestion: Question = { id: newId, text: '', type: 'spoken', timeMinutes: 2 };

    setLoadingQuestionIds((prev) => new Set(prev).add(newId));

    // Read fresh state after potential skill creation
    const freshSkills = getValues(`sections.${sectionIndex}.skills`);
    setValue(
      `sections.${sectionIndex}.skills.${skillIndex}.questions`,
      [...freshSkills[skillIndex].questions, newQuestion],
      { shouldDirty: true },
    );

    // Append to visual order so the new question appears at the end of the section
    const sectionId = sections[sectionIndex].id;
    setVisualOrderMap((prev) => {
      const currentOrder = prev[sectionId];
      if (currentOrder) {
        return { ...prev, [sectionId]: [...currentOrder, newId] };
      }
      // Initialize visual order from current items + new question at end
      const existingIds = sections[sectionIndex].skills.flatMap((s) => s.questions.map((q) => q.id));
      return { ...prev, [sectionId]: [...existingIds, newId] };
    });

    const capturedSkillName = skillName;
    const capturedSkillIndex = skillIndex;

    // Call real AI generation API
    const language = getValues('language') || 'EN';
    const sectionSkills = getValues(`sections.${sectionIndex}.skills`);
    const existingQuestions = sectionSkills
      .flatMap((s) => s.questions.map((q) => q.text))
      .filter(Boolean);

    // Send section_name + existing_types so the backend applies modality guardrails
    const sectionName = sections[sectionIndex]?.name || '';
    const existingTypes = sectionSkills
      .flatMap((s) => s.questions.map((q) => q.type))
      .filter(Boolean);

    generateQuestion.mutate(
      { skill_name: capturedSkillName, language, previous_questions: existingQuestions, section_name: sectionName, existing_types: existingTypes },
      {
        onSuccess: (data) => {
          const currentSections = getValues('sections');
          const currentQuestions = currentSections[sectionIndex].skills[capturedSkillIndex].questions;
          setValue(
            `sections.${sectionIndex}.skills.${capturedSkillIndex}.questions`,
            currentQuestions.map((q) => q.id === newId ? {
              ...q,
              text: data.question_text,
              type: data.type,
              timeMinutes: data.time_minutes,
              ...(data.code_snippet ? { codeSnippet: data.code_snippet, codingLanguage: data.coding_language } : {}),
            } : q),
            { shouldDirty: true },
          );
          setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(newId); return n; });
          setAiGeneratedIds((prev) => new Set(prev).add(newId));
        },
        onError: () => {
          // On failure, remove the skeleton question
          setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(newId); return n; });
          toast.error(t('exam_config.toasts.generate_failed'));
        },
      },
    );
  }, [sections, setValue, getValues, generateQuestion]);

  // ── Flow A: Regenerate ──
  const handleRegenerate = useCallback((sectionIndex: number, skillIndex: number, questionId: string) => {
    const skill = sections[sectionIndex].skills[skillIndex];
    setLoadingQuestionIds((prev) => new Set(prev).add(questionId));

    const language = getValues('language') || 'EN';
    const skillQuestions = sections[sectionIndex].skills[skillIndex].questions
      .map((q) => q.text)
      .filter(Boolean);

    generateQuestion.mutate(
      { skill_name: skill.name, language, previous_questions: skillQuestions },
      {
        onSuccess: (data) => {
          const currentSections = getValues('sections');
          const currentQuestions = currentSections[sectionIndex].skills[skillIndex].questions;
          setValue(
            `sections.${sectionIndex}.skills.${skillIndex}.questions`,
            currentQuestions.map((q) => q.id === questionId ? {
              ...q,
              text: data.question_text,
              type: data.type,
              timeMinutes: data.time_minutes,
              ...(data.code_snippet ? { codeSnippet: data.code_snippet, codingLanguage: data.coding_language } : {}),
            } : q),
            { shouldDirty: true },
          );
          setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
        },
        onError: () => {
          setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
          toast.error(t('exam_config.toasts.regenerate_failed'));
        },
      },
    );
  }, [sections, setValue, getValues, generateQuestion]);

  // ── Flow B: Add without skill (appends to last skill so it appears at section end) ──
  const handleAddWithoutSkill = useCallback((sectionIndex: number) => {
    const sectionSkills = sections[sectionIndex].skills;
    const lastSkillIndex = sectionSkills.length - 1;
    const lastSkill = sectionSkills[lastSkillIndex];
    if (!lastSkill) return;

    const newId = uuid();
    const newQuestion: Question = { id: newId, text: '', type: 'spoken', timeMinutes: 2 };

    setValue(
      `sections.${sectionIndex}.skills.${lastSkillIndex}.questions`,
      [...lastSkill.questions, newQuestion],
      { shouldDirty: true },
    );
    setUnassignedIds((prev) => new Set(prev).add(newId));

    // Append to visual order so save preserves position
    const sectionId = sections[sectionIndex].id;
    setVisualOrderMap((prev) => {
      const currentOrder = prev[sectionId];
      if (currentOrder) {
        return { ...prev, [sectionId]: [...currentOrder, newId] };
      }
      const existingIds = sections[sectionIndex].skills.flatMap((s) => s.questions.map((q) => q.id));
      return { ...prev, [sectionId]: [...existingIds, newId] };
    });
  }, [sections, setValue]);

  // ── Flow B: Auto-assign skill on blur ──
  const handleAutoAssignSkill = useCallback((sectionIndex: number, questionId: string) => {
    if (assigningSkillIds.has(questionId)) return;
    setAssigningSkillIds((prev) => new Set(prev).add(questionId));

    const freshSections = getValues('sections');
    const freshSection = freshSections[sectionIndex];

    // Find the question in its current location
    let currentSourceSkillIdx = -1;
    let currentSourceQIdx = -1;
    for (let ski = 0; ski < freshSection.skills.length; ski++) {
      const qi = freshSection.skills[ski].questions.findIndex((q) => q.id === questionId);
      if (qi !== -1) { currentSourceSkillIdx = ski; currentSourceQIdx = qi; break; }
    }
    if (currentSourceSkillIdx === -1) return;

    const freshQuestion = freshSection.skills[currentSourceSkillIdx].questions[currentSourceQIdx];

    assignSkill.mutate(
      {
        question_text: freshQuestion.text,
        available_skills: freshSection.skills.map((s) => ({ id: s.id, name: s.name })),
        section_name: sections[sectionIndex].name,
      },
      {
        onSuccess: (data) => {
          // Re-read current form state to avoid overwriting edits made during the async call
          const currentSections = getValues('sections');
          const currentSection = currentSections[sectionIndex];

          // Find the question's CURRENT location (may have changed since mutation fired)
          let srcSkillIdx = -1;
          let srcQIdx = -1;
          let currentQuestion: Question | null = null;
          for (let ski = 0; ski < currentSection.skills.length; ski++) {
            const qi = currentSection.skills[ski].questions.findIndex((q) => q.id === questionId);
            if (qi !== -1) {
              srcSkillIdx = ski;
              srcQIdx = qi;
              currentQuestion = currentSection.skills[ski].questions[qi];
              break;
            }
          }
          if (srcSkillIdx === -1 || !currentQuestion) return;

          // Backend returns suggested_new_skill only when it recommends creating
          // a new skill (section-aware threshold applied server-side).
          if (data.suggested_new_skill) {
            const newSkillId = uuid();
            const newSkill = { id: newSkillId, name: data.suggested_new_skill, questions: [currentQuestion] };
            // Remove question from current skill
            const sourceQuestions = currentSection.skills[srcSkillIdx].questions.filter((_, i) => i !== srcQIdx);
            setValue(`sections.${sectionIndex}.skills.${srcSkillIdx}.questions`, sourceQuestions, { shouldDirty: true });
            // Add new skill with the question
            const updatedSkills = [...getValues(`sections.${sectionIndex}.skills`), newSkill];
            setValue(`sections.${sectionIndex}.skills`, updatedSkills, { shouldDirty: true });
            toast.success(t('exam_config.toasts.created_skill', { skill: data.suggested_new_skill }));
          } else {
            const targetSkillIndex = currentSection.skills.findIndex((s) => s.id === data.skill_id);
            if (targetSkillIndex !== -1 && targetSkillIndex !== srcSkillIdx) {
              const sourceQuestions = currentSection.skills[srcSkillIdx].questions.filter((_, i) => i !== srcQIdx);
              const targetQuestions = [...currentSection.skills[targetSkillIndex].questions, currentQuestion];
              setValue(`sections.${sectionIndex}.skills.${srcSkillIdx}.questions`, sourceQuestions, { shouldDirty: true });
              setValue(`sections.${sectionIndex}.skills.${targetSkillIndex}.questions`, targetQuestions, { shouldDirty: true });
            }
          }
          setAssigningSkillIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
          setUnassignedIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });

          // Store LLM-suggested modality on the question object so it persists with config_data
          if (data.suggested_type) {
            const postSections = getValues('sections');
            const postSection = postSections[sectionIndex];
            for (let ski = 0; ski < postSection.skills.length; ski++) {
              const qi = postSection.skills[ski].questions.findIndex((q) => q.id === questionId);
              if (qi !== -1) {
                const q = postSection.skills[ski].questions[qi];
                const suggestedValue = data.suggested_type !== q.type ? data.suggested_type : undefined;
                setValue(
                  `sections.${sectionIndex}.skills.${ski}.questions.${qi}.suggestedType` as any,
                  suggestedValue,
                  { shouldDirty: true },
                );
                break;
              }
            }
          }
        },
        onError: () => {
          setAssigningSkillIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
          toast.error(t('exam_config.toasts.assign_failed'));
        },
      },
    );
  }, [getValues, setValue, assigningSkillIds, assignSkill]);

  const handleLanguageChange = useCallback((newLanguage: string) => {
    const currentLanguage = getValues('language');
    if (newLanguage === currentLanguage) return;

    // Show warning if there are any questions in the config (they'll be regenerated)
    const hasQuestions = getValues('sections')?.some(
      (section) => section.skills?.some((skill) => skill.questions?.length > 0),
    );
    if (hasQuestions) {
      setPendingLanguage(newLanguage);
      setShowLanguageWarning(true);
    } else {
      setValue('language', newLanguage, { shouldDirty: true });
    }
  }, [getValues, setValue]);

  const confirmLanguageChange = useCallback(() => {
    if (!pendingLanguage) return;

    // Close dialog immediately and set the new language
    setShowLanguageWarning(false);
    setValue('language', pendingLanguage, { shouldDirty: true });

    // Collect all questions with their full form paths upfront (before any mutations).
    // For code questions, capture existing code so we can preserve it after translation.
    const currentSections = getValues('sections');
    const questionJobs: Array<{
      basePath: string;
      questionId: string;
      skillName: string;
      existingText: string;
      isCodeQuestion: boolean;
      existingCodeSnippet?: string;
      existingCodingLanguage?: string;
    }> = [];

    currentSections.forEach((section, si) => {
      section.skills.forEach((skill, ski) => {
        skill.questions.forEach((q, qi) => {
          const isCode = q.type === 'code-read-only' || q.type === 'code-write';
          questionJobs.push({
            basePath: `sections.${si}.skills.${ski}.questions.${qi}`,
            questionId: q.id,
            skillName: skill.name,
            existingText: q.text || '',
            isCodeQuestion: isCode,
            ...(isCode ? { existingCodeSnippet: q.codeSnippet, existingCodingLanguage: q.codingLanguage } : {}),
          });
        });
      });
    });

    // Mark all questions as loading (skeleton)
    setLoadingQuestionIds((prev) => {
      const next = new Set(prev);
      questionJobs.forEach((j) => next.add(j.questionId));
      return next;
    });

    // Fire individual generate-question calls for each question
    const newLang = pendingLanguage;
    setPendingLanguage(null);

    // Use mutateAsync — mutate() replaces callbacks on repeated calls to the same instance,
    // so only the last one's onSuccess fires. mutateAsync returns a per-call promise.
    questionJobs.forEach(({ basePath, questionId, skillName, existingText, isCodeQuestion, existingCodeSnippet, existingCodingLanguage }) => {
      generateQuestion.mutateAsync(
        {
          skill_name: skillName,
          language: newLang,
          previous_questions: [],
          // If the question has custom text, translate it instead of generating new
          ...(existingText ? { translate_text: existingText } : {}),
        },
      ).then((data) => {
        // Only translate the question text; preserve type, time, and code fields
        setValue(`${basePath}.text` as any, data.question_text, { shouldDirty: true });
        setValue(`${basePath}.type` as any, data.type, { shouldDirty: true });
        setValue(`${basePath}.timeMinutes` as any, data.time_minutes, { shouldDirty: true });

        if (isCodeQuestion) {
          // Code is language-agnostic — keep the original snippet and language intact
          if (existingCodeSnippet) {
            setValue(`${basePath}.codeSnippet` as any, existingCodeSnippet, { shouldDirty: true });
          }
          if (existingCodingLanguage) {
            setValue(`${basePath}.codingLanguage` as any, existingCodingLanguage, { shouldDirty: true });
          }
        } else if (data.code_snippet) {
          setValue(`${basePath}.codeSnippet` as any, data.code_snippet, { shouldDirty: true });
          setValue(`${basePath}.codingLanguage` as any, data.coding_language, { shouldDirty: true });
        }
        setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
      }).catch(() => {
        setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(questionId); return n; });
        toast.error(t('exam_config.toasts.regenerate_for_skill_failed', { skill: skillName }));
      });
    });

    toast.success(
      t('exam_config.toasts.regenerating_questions', {
        count: questionJobs.length,
        language: newLang === 'FR' ? t('exam_config.languages.french') : t('exam_config.languages.english'),
      }),
    );
  }, [pendingLanguage, getValues, setValue, generateQuestion]);

  const handleUpdateQuestion = useCallback((sectionIndex: number, skillIndex: number, questionIndex: number, updates: Partial<Question>) => {
    const freshSections = getValues('sections');
    const skill = freshSections[sectionIndex].skills[skillIndex];
    // Clear suggestedType when the user changes the question's type (via modality chip or Switch button)
    const finalUpdates = updates.type ? { ...updates, suggestedType: undefined } : updates;
    const updatedQuestions = skill.questions.map((q, i) =>
      i === questionIndex ? { ...q, ...finalUpdates } : q
    );
    setValue(`sections.${sectionIndex}.skills.${skillIndex}.questions`, updatedQuestions, { shouldDirty: true });
  }, [getValues, setValue]);

  const handleRemoveQuestion = useCallback((sectionIndex: number, skillIndex: number, questionIndex: number) => {
    const skill = sections[sectionIndex].skills[skillIndex];
    const removedId = skill.questions[questionIndex]?.id;
    setValue(
      `sections.${sectionIndex}.skills.${skillIndex}.questions`,
      skill.questions.filter((_, i) => i !== questionIndex),
      { shouldDirty: true },
    );
    if (removedId) {
      setAiGeneratedIds((prev) => { const n = new Set(prev); n.delete(removedId); return n; });
      setUnassignedIds((prev) => { const n = new Set(prev); n.delete(removedId); return n; });
      setLoadingQuestionIds((prev) => { const n = new Set(prev); n.delete(removedId); return n; });
    }
  }, [sections, setValue]);

  // Drag and drop — reorders visual position within a section.
  // Sets hasVisualReorder=true to enable the save button; actual ordered structure is built at save time.
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeItem = allQuestionItems.find((item) => item.question.id === active.id);
    const overItem = allQuestionItems.find((item) => item.question.id === over.id);
    if (!activeItem || !overItem) return;

    // Only allow reorder within the same section
    if (activeItem.sectionIndex !== overItem.sectionIndex) return;

    const si = activeItem.sectionIndex;
    const sectionId = sections[si].id;

    // Get current visual order for this section
    const currentOrder = allQuestionItems
      .filter((item) => item.sectionIndex === si)
      .map((item) => item.question.id);

    const oldIdx = currentOrder.indexOf(active.id as string);
    const newIdx = currentOrder.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;

    const reordered = arrayMove(currentOrder, oldIdx, newIdx);

    setVisualOrderMap((prev) => ({ ...prev, [sectionId]: reordered }));
    setHasVisualReorder(true);
  }, [allQuestionItems, sections]);

  // Summary stats (interview questions + psychometric tests)
  const attachedPsychometricTests = (data?.config_data?.psychometric_sections ?? [])
    .map((s) => allPsychometricTests.find((t) => t.id === s.test_id))
    .filter(Boolean);
  const totalQuestions = sections.reduce((sum, s) => sum + s.skills.reduce((ss, sk) => ss + sk.questions.length, 0), 0)
    + attachedPsychometricTests.reduce((sum, t) => sum + t!.question_count, 0);
  const totalMinutes = sections.reduce((sum, s) => sum + s.skills.reduce((ss, sk) => ss + sk.questions.reduce((qs, q) => qs + q.timeMinutes, 0), 0), 0)
    + attachedPsychometricTests.reduce((sum, t) => sum + t!.estimated_minutes, 0);
  const allQuestionIds = allQuestionItems.map((item) => item.question.id);

  // Tab state — default to first section
  const [activeTabId, setActiveTabId] = useState<string>('');
  const [skillsPickerOpen, setSkillsPickerOpen] = useState(false);
  useEffect(() => {
    if (sections.length > 0 && !activeTabId) {
      setActiveTabId(sections[0].id);
    }
  }, [sections, activeTabId]);

  // Scroll to the absolute bottom after adding a question or when it finishes generating
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  // Trigger on new question added (skeleton appears)
  useEffect(() => {
    if (pendingScrollRef.current) {
      pendingScrollRef.current = false;
      scrollToBottom();
    }
  }, [allQuestionIds.length, scrollToBottom]);

  // Re-trigger when a question finishes generating (skeleton → full card, height changes)
  useEffect(() => {
    if (loadingQuestionIds.size === 0 && allQuestionIds.length > 0) {
      // A question just finished loading — scroll again to reveal the full content
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingQuestionIds.size, scrollToBottom]);

  const psychometricTabId = '__psychometric__';
  const hasPsychometric = allPsychometricTests.length > 0;
  const psychometricMinutes = attachedPsychometricTests.reduce((sum, t) => sum + t!.estimated_minutes, 0);
  const psychometricTestCount = (data?.config_data?.psychometric_sections ?? []).length;

  // Build tab definitions
  const SECTION_ICONS: Record<string, ReactNode> = {
    technical: <GraduationCap className="w-4 h-4" />,
    soft: <Users className="w-4 h-4" />,
  };
  const sectionTabs: SectionTab[] = sections.map((section, i) => {
    const meta = SECTION_META[section.name];
    const qCount = section.skills.reduce((sum, sk) => sum + sk.questions.length, 0);
    const mins = section.skills.reduce((sum, sk) => sum + sk.questions.reduce((qs, q) => qs + q.timeMinutes, 0), 0);
    return {
      id: section.id,
      number: meta?.number ?? String(i + 1).padStart(2, '0'),
      title: SECTION_TITLE_KEYS[section.name] ? t(SECTION_TITLE_KEYS[section.name]) : section.name,
      icon: SECTION_ICONS[meta?.icon ?? 'technical'],
      stats: t('exam_config.rail.questions_minutes', { count: qCount, minutes: mins }),
    };
  });
  if (hasPsychometric) {
    sectionTabs.push({
      id: psychometricTabId,
      number: String(sections.length + 1).padStart(2, '0'),
      title: t('exam_config.psychometric.title'),
      icon: <Brain className="w-4 h-4" />,
      stats: psychometricTestCount > 0
        ? t('exam_config.psychometric.tests_minutes', { count: psychometricTestCount, minutes: psychometricMinutes })
        : t('exam_config.psychometric.no_tests'),
    });
  }

  let globalCounter = 0;

  return (
    <div className="flex flex-col h-full bg-light" data-tour="exam-config-page">
      {/* Language-switch warning dialog */}
      <ConfirmationDialog
        isOpen={showLanguageWarning}
        onClose={() => { setShowLanguageWarning(false); setPendingLanguage(null); }}
        title={t('exam_config.dialogs.change_language_title')}
        message={
          <>
            {t('exam_config.dialogs.change_language_message', {
              language: pendingLanguage === 'FR' ? t('exam_config.languages.french') : t('exam_config.languages.english'),
            })}
          </>
        }
        buttons={[
          {
            label: t('exam_config.actions.cancel'),
            onClick: () => { setShowLanguageWarning(false); setPendingLanguage(null); },
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: t('exam_config.actions.regenerate_all'),
            onClick: confirmLanguageChange,
            intent: 'secondary',
          },
        ]}
      />

      {/* Save confirmation dialog */}
      <ConfirmationDialog
        isOpen={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        title={t('exam_config.dialogs.save_title')}
        message={t('exam_config.dialogs.save_message')}
        buttons={[
          {
            label: t('exam_config.actions.cancel'),
            onClick: () => setShowSaveConfirm(false),
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: t('exam_config.actions.save'),
            onClick: () => {
              setShowSaveConfirm(false);
              handleSubmit(onSubmit)();
            },
            intent: 'secondary',
          },
        ]}
      />

      {/* Close with unsaved changes confirmation dialog */}
      {onBack && (
        <ConfirmationDialog
          isOpen={showCloseConfirm}
          onClose={() => setShowCloseConfirm(false)}
          title={t('exam_config.dialogs.unsaved_title')}
          message={t('exam_config.dialogs.unsaved_message')}
          closeLabel={t('exam_config.actions.close')}
          buttons={[
            {
              label: t('exam_config.actions.discard_close'),
              onClick: () => {
                setShowCloseConfirm(false);
                onBack();
              },
              type: 'outlined',
              intent: 'danger',
            },
            {
              label: t('exam_config.actions.save_close'),
              onClick: () => {
                setShowCloseConfirm(false);
                handleSubmit(onSubmit)();
              },
              intent: 'secondary',
            },
          ]}
        />
      )}

      {/* Body — section rail + content */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0">
        {/* Left: vertical section rail */}
        {sectionTabs.length > 0 && (
          <ExamSectionRail
            tabs={sectionTabs}
            activeTabId={activeTabId}
            onTabChange={setActiveTabId}
            language={language}
            onLanguageChange={handleLanguageChange}
            interviewExpiryDays={interviewExpiryDays}
            onInterviewExpiryDaysChange={handleExpiryChange}
            totalQuestions={totalQuestions}
            totalMinutes={totalMinutes}
            onSave={() => setShowSaveConfirm(true)}
            saveDisabled={(!isDirty && !hasVisualReorder) || updateMutation.isPending}
            saving={updateMutation.isPending}
          />
        )}

        {/* Right: content pane */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div ref={scrollContainerRef} className="flex-1 overflow-y-scroll bg-light [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-dark-invirtus/15 [&::-webkit-scrollbar-track]:bg-transparent">
            <div className="px-4 lg:px-6 py-5">

          {/* Question section panels — all stay mounted for DnD, only active is visible */}
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={allQuestionIds} strategy={verticalListSortingStrategy}>
              {sections.map((section, sectionIndex) => {
                const isActive = activeTabId === section.id;

                return (
                  <div
                    key={section.id}
                    className={isActive ? '' : 'h-0 overflow-hidden pointer-events-none'}
                    aria-hidden={!isActive}
                  >
                    {/* Questions — product tour spotlights the first Technical Skills card only */}
                    <div className="space-y-2.5">
                      {allQuestionItems
                        .filter((item) => item.sectionIndex === sectionIndex)
                        .map((item, idx, arr) => {
                          const currentGlobal = globalCounter++;
                          const isUnassigned = unassignedIds.has(item.question.id);
                          const isPerCand = PER_CANDIDATE_SKILLS.has(item.skillName);
                          const isLast = idx === arr.length - 1;
                          const tourFirstTechnical =
                            section.name === 'Technical Skills' && idx === 0;
                          return (
                            <div
                              key={item.question.id}
                              ref={isLast ? lastQuestionRef : undefined}
                              {...(tourFirstTechnical ? { 'data-tour': 'exam-config-questions-technical' } : {})}
                            >
                              <QuestionEditor
                                question={item.question}
                                index={currentGlobal}
                                skillName={isUnassigned ? undefined : item.skillName}
                                isLoading={loadingQuestionIds.has(item.question.id)}
                                isAiGenerated={aiGeneratedIds.has(item.question.id)}
                                isAssigningSkill={assigningSkillIds.has(item.question.id)}
                                isPerCandidate={isPerCand}
                                startInEditMode={isUnassigned && !item.question.text}
                                suggestedType={item.question.suggestedType}
                                onUpdate={(updates) => handleUpdateQuestion(sectionIndex, item.skillIndex, item.questionIndex, updates)}
                                onRemove={() => handleRemoveQuestion(sectionIndex, item.skillIndex, item.questionIndex)}
                                onRegenerate={() => handleRegenerate(sectionIndex, item.skillIndex, item.question.id)}
                                onTextBlur={isUnassigned ? () => handleAutoAssignSkill(sectionIndex, item.question.id) : undefined}
                              />
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                );
              })}
            </SortableContext>
          </DndContext>

          {/* Psychometric panel */}
          {hasPsychometric && (
            <div className={activeTabId === psychometricTabId ? '' : 'h-0 overflow-hidden pointer-events-none'} aria-hidden={activeTabId !== psychometricTabId}>
              <ImportFromLibrary
                language={language}
                allTests={allPsychometricTests}
                attachedTestIds={new Set((data?.config_data?.psychometric_sections ?? []).map((s) => s.test_id))}
                onAdd={(testId) => addPsychometricMutation.mutate(testId)}
                onRemove={(testId) => removePsychometricMutation.mutate(testId)}
                onPreview={(testId) => setPreviewTestId(testId)}
                isAdding={addPsychometricMutation.isPending}
                isRemoving={removePsychometricMutation.isPending}
              />
            </div>
          )}

        </div>
      </div>

      {/* Add question zone — bottom of the content pane */}
      {(() => {
        const activeSectionIndex = sections.findIndex((s) => s.id === activeTabId);
        if (activeSectionIndex === -1) return null;

        const section = sections[activeSectionIndex];
        const existingNames = new Set(section.skills.map((s) => s.name));
        const toTitleCase = (str: string) => str.replace(/\b\w/g, (c) => c.toUpperCase());

        const suggestedPool: string[] = [];
        if (section.name === 'Technical Skills') {
          for (const name of CORE_SKILL_NAMES) {
            if (!existingNames.has(name)) suggestedPool.push(name);
          }
          for (const name of (suggestedSkills?.technical || [])) {
            if (!existingNames.has(name) && !CORE_SKILL_NAMES.includes(name)) suggestedPool.push(name);
          }
        } else if (section.name === 'Soft Skills') {
          for (const name of (suggestedSkills?.soft || [])) {
            if (!existingNames.has(name)) suggestedPool.push(name);
          }
        }

        const allSkills = [
          ...section.skills.map((s) => ({ id: s.id, name: toTitleCase(s.name) })),
          ...suggestedPool.map((name) => ({ id: name, name })),
        ];

        return (
          <div className="shrink-0 bg-light border-t border-light-lila/40">
            <div className="px-4 lg:px-6 py-3 flex items-stretch gap-2">
              {!skillsPickerOpen && (
                <Button
                  type="outlined"
                  intent="secondary"
                  size="md"
                  dashed
                  className="flex-1"
                  icon={<Plus />}
                  label={t('exam_config.add_question.label')}
                  onClick={() => {
                    handleAddWithoutSkill(activeSectionIndex);
                    pendingScrollRef.current = true;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { 
                      e.preventDefault(); handleAddWithoutSkill(activeSectionIndex);
                       pendingScrollRef.current = true; 
                      }
                  }}
                />
              )}
              <div className={skillsPickerOpen ? 'w-full' : 'flex-1'}>
                <AddQuestionDropdown
                  zone
                  skills={allSkills}
                  onAddWithSkill={(skillIdOrName) => { handleAddWithSkill(activeSectionIndex, skillIdOrName); pendingScrollRef.current = true; }}
                  onOpenChange={setSkillsPickerOpen}
                />
              </div>
            </div>
          </div>
        );
      })()}
        </div>
      </div>

      {/* Psychometric test preview modal */}
      <PreviewModal
        testId={previewTestId}
        language={language}
        onClose={() => setPreviewTestId(null)}
      />
    </div>
  );
});
